import { useId, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiRequestError, apiRequest } from '../lib/api-client';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { ArrowIcon, CheckIcon, EyeIcon, LockIcon } from './icons';

/**
 * Choosing a new password from a reset link.
 *
 * The confirmation field is checked here so a typo costs a keystroke rather than another round trip
 * and another e-mail; everything else — how strong the password has to be, whether the link is still
 * good — is the server's answer, because the browser is not where that is decided.
 */

const MESSAGES: Record<string, string> = {
  'auth.invalid_token': 'reset.invalid',
  'auth.token_already_used': 'reset.used',
  'auth.token_expired': 'reset.expired',
};

export function ResetPasswordPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const passwordId = useId();
  const confirmId = useId();

  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setReasons([]);

    if (password !== confirmation) {
      setError(t('reset.mismatch'));
      return;
    }

    setSubmitting(true);

    try {
      await apiRequest('/v1/auth/password/reset', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'auth.weak_password') {
        const details = caught.payload?.details as { reasons?: string[] } | undefined;

        setReasons(details?.reasons ?? []);
        setError(t('signup.weakPassword'));
        return;
      }

      const key = caught instanceof ApiRequestError ? MESSAGES[caught.code] : undefined;

      setError(t(key ?? 'error.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <span className="auth__done-mark" aria-hidden="true">
            <CheckIcon />
          </span>

          <h1>{t('reset.doneTitle')}</h1>
          <p>{t('reset.doneBody')}</p>

          <Link className="button button--primary" to="/entrar">
            {t('reset.goToLogin')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Nothing to submit without a token, and saying so beats a form that can only fail.
  if (!token) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <h1>{t('reset.title')}</h1>
          <p>{t('reset.missingToken')}</p>

          <Link className="button button--primary" to="/esqueci-senha">
            {t('reset.requestAnother')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="auth__heading">
        <h1>{t('reset.title')}</h1>
        <p>{t('reset.subtitle')}</p>
      </header>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="input">
          <label htmlFor={passwordId}>{t('reset.newPassword')}</label>
          <div className="input__control">
            <LockIcon />
            <input
              id={passwordId}
              type={revealed ? 'text' : 'password'}
              name="new-password"
              autoComplete="new-password"
              autoFocus
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              className="input__reveal"
              onClick={() => setRevealed((current) => !current)}
              aria-label={revealed ? t('login.hidePassword') : t('login.showPassword')}
              aria-pressed={revealed}
            >
              <EyeIcon crossed={revealed} />
            </button>
          </div>
          <p className="input__hint">{t('signup.passwordHint')}</p>
        </div>

        <div className="input">
          <label htmlFor={confirmId}>{t('reset.confirmPassword')}</label>
          <div className="input__control">
            <LockIcon />
            <input
              id={confirmId}
              type={revealed ? 'text' : 'password'}
              name="confirm-password"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="alert" role="alert">
            <p>{error}</p>
            {reasons.length > 0 && (
              <ul>
                {reasons.map((reason) => (
                  <li key={reason}>{t(`password.reason.${reason}`)}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting ? t('reset.submitting') : t('reset.submit')}
          {!submitting && <ArrowIcon />}
        </button>
      </form>

      <p className="auth__switch">
        <Link to="/entrar">{t('forgot.backToLogin')}</Link>
      </p>
    </AuthLayout>
  );
}
